import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import { useLegacyCases } from "../../../hooks/useLegacyCases";
import type { LegacyCaseState } from "../../../types/legacyCases";
import { formatLegacyDate } from "../../../utils/legacyReviewForm";
import LegacyCaseListCard from "../../../components/admin/legacy-cases/LegacyCaseListCard";

const STATUS_LABELS = {
  archived: "Not reviewed",
  reviewed: "Draft saved",
  imported: "Imported",
};
const STATUS_COLORS = { archived: "gray", reviewed: "blue", imported: "green" };

export default function LegacyCasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const selectedStatus = searchParams.get("status");
  const status =
    selectedStatus &&
    Object.prototype.hasOwnProperty.call(STATUS_LABELS, selectedStatus)
      ? (selectedStatus as LegacyCaseState)
      : undefined;
  const page = Math.max(
    1,
    Number.parseInt(searchParams.get("page") || "1", 10) || 1
  );
  const filters = useMemo(
    () => ({
      page,
      limit: 25,
      search: debouncedSearch.trim() || undefined,
      status,
    }),
    [debouncedSearch, page, status]
  );
  const query = useLegacyCases(filters);
  const rows = useMemo(() => query.data?.data ?? [], [query.data]);
  const total = useMemo(() => query.data?.pagination.total ?? 0, [query.data]);
  const pages = useMemo(
    () => Math.max(1, query.data?.pagination.totalPages ?? 1),
    [query.data]
  );
  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value) next.set("search", value);
          else next.delete("search");
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const handleStatus = useCallback(
    (value: string | null) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (value) next.set("status", value);
        else next.delete("status");
        next.delete("page");
        return next;
      });
    },
    [setSearchParams]
  );
  const handlePage = useCallback(
    (value: number) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.set("page", String(value));
        return next;
      });
    },
    [setSearchParams]
  );
  const handleRetry = useCallback(() => {
    void query.refetch();
  }, [query]);
  const handleClear = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  return (
    <Stack gap="lg">
      <Title order={1}>Legacy Cases</Title>
      <Group align="flex-end">
        <TextInput
          label="Search NovoPath cases"
          placeholder="Case number or patient name"
          leftSection={<IconSearch size={18} />}
          value={search}
          onChange={handleSearch}
          style={{ flex: 1, minWidth: 240 }}
        />
        <Select
          label="Review status"
          placeholder="All cases"
          clearable
          value={status ?? null}
          onChange={handleStatus}
          data={[
            { value: "reviewed", label: "Draft saved" },
            { value: "imported", label: "Imported" },
            { value: "archived", label: "Not reviewed" },
          ]}
        />
        {(search || status) && (
          <Button variant="subtle" onClick={handleClear}>
            Clear filters
          </Button>
        )}
      </Group>
      <Paper withBorder p="md">
        <Stack gap="md" aria-busy={query.isFetching}>
          <Group justify="space-between">
            <Text aria-live="polite">
              {total.toLocaleString()} {total === 1 ? "case" : "cases"}
            </Text>
            {query.isFetching && !query.isLoading && (
              <Loader size="xs" aria-label="Updating cases" />
            )}
          </Group>
          {query.isLoading ? (
            <Center mih={220}>
              <Loader aria-label="Loading legacy cases" />
            </Center>
          ) : query.error ? (
            <Alert color="red" title="Cases unavailable" role="alert">
              <Stack align="flex-start">
                <Text size="sm">{query.error.message}</Text>
                <Button onClick={handleRetry}>Retry</Button>
              </Stack>
            </Alert>
          ) : rows.length === 0 ? (
            <Text ta="center" py="xl" c="dimmed">
              {search || status
                ? "No legacy cases match these filters."
                : "No NovoPath cases have been archived yet."}
            </Text>
          ) : (
            <>
              <Stack hiddenFrom="sm">
                {rows.map((row) => (
                  <LegacyCaseListCard key={row.id} item={row} />
                ))}
              </Stack>
              <Box visibleFrom="sm">
                <Table.ScrollContainer minWidth={760}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Case number</Table.Th>
                        <Table.Th>Patient</Table.Th>
                        <Table.Th>Date of birth</Table.Th>
                        <Table.Th>Received</Table.Th>
                        <Table.Th>Review</Table.Th>
                        <Table.Th>Documents</Table.Th>
                        <Table.Th>Action</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td>
                            <Text fw={600}>{row.caseNumber}</Text>
                          </Table.Td>
                          <Table.Td>
                            {[row.firstName, row.middleInitial, row.lastName]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </Table.Td>
                          <Table.Td>
                            {formatLegacyDate(row.dateOfBirth)}
                          </Table.Td>
                          <Table.Td>
                            {formatLegacyDate(row.dateReceived)}
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={4}>
                              <Badge
                                color={STATUS_COLORS[row.status]}
                                variant="light"
                              >
                                {STATUS_LABELS[row.status]}
                              </Badge>
                              {row.hasConflicts && (
                                <Badge color="orange" variant="light">
                                  Source conflicts
                                </Badge>
                              )}
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            {row.hasReport
                              ? "Report available"
                              : "Report missing"}
                          </Table.Td>
                          <Table.Td>
                            <Button
                              component={Link}
                              to={`/admin/legacy-cases/${row.id}`}
                              variant="light"
                              aria-label={`Review legacy case ${row.caseNumber}`}
                            >
                              Open
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Box>
            </>
          )}
          {pages > 1 && (
            <Group justify="flex-end">
              <Pagination total={pages} value={page} onChange={handlePage} />
            </Group>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
